import React, { useState, useEffect } from 'react';
import { AgentRoleFields } from '@/components/kypa/AgentRoleFields';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Bot, Shield, Check, Loader2, AlertTriangle, Plus, X, ShieldCheck, Award,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FRAMEWORKS = ['LangChain', 'CrewAI', 'AutoGPT', 'LangGraph', 'Protocol-native', 'Custom'];
const CAPABILITY_OPTIONS = [
  'Trading', 'Research', 'Data Analysis', 'Market Making', 'Risk Assessment',
  'Document Processing', 'Customer Service', 'Content Generation', 'Code Generation',
  'Supply Chain', 'Logistics', 'Financial Analysis', 'Compliance', 'Auditing',
  'Arbitrage', 'Portfolio Management', 'Quantitative Analysis', 'NLP', 'Computer Vision',
];

const PLEDGE_TIERS = [
  { name: 'Bronze', range: '$500 – $4,999', minUsdc: 500, color: 'text-amber-500', border: 'border-amber-700/40', bg: 'from-amber-800/20 to-amber-900/5' },
  { name: 'Silver', range: '$5,000 – $24,999', minUsdc: 5000, color: 'text-slate-300', border: 'border-slate-400/40', bg: 'from-slate-400/15 to-slate-500/5' },
  { name: 'Gold', range: '$25,000 – $99,999', minUsdc: 25000, color: 'text-yellow-400', border: 'border-yellow-500/40', bg: 'from-yellow-500/15 to-yellow-600/5' },
  { name: 'Platinum', range: '$100,000+', minUsdc: 100000, color: 'text-cyan-300', border: 'border-cyan-400/40', bg: 'from-cyan-400/15 to-blue-500/10' },
];

export default function RegisterAgent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'kypa' ? 'kypa' : 'credit';
  const [mode, setMode] = useState(initialMode);
  const token = localStorage.getItem('kutro_token');
  const [form, setForm] = useState({
    agent_address: '', name: '', description: '', framework: '',
    staked_kutro: 0, website: '', contact_email: '', capabilities: [],
    agent_role: 'buyer', merchant_domain: '',
    pledge_tier: 'Bronze', enrollment_type: initialMode === 'kypa' ? 'kypa' : 'credit',
  });
  const [customCap, setCustomCap] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm((f) => ({ ...f, enrollment_type: mode }));
  }, [mode]);

  const toggleCap = (cap) => {
    setForm(f => ({
      ...f,
      capabilities: f.capabilities.includes(cap) ? f.capabilities.filter(c => c !== cap) : [...f.capabilities, cap],
    }));
  };

  const addCustomCap = () => {
    if (customCap.trim() && !form.capabilities.includes(customCap.trim())) {
      setForm(f => ({ ...f, capabilities: [...f.capabilities, customCap.trim()] }));
      setCustomCap('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'credit' && !token) { setError('Please log in to register an agent for Credit.'); return; }
    if (!form.agent_address || !form.name) { setError('Agent address and name are required.'); return; }
    if (!form.agent_address.startsWith('0x') || form.agent_address.length !== 42) { setError('Invalid Ethereum address (must be 0x + 40 hex chars).'); return; }

    setSubmitting(true); setError(null);
    try {
      if (mode === 'kypa') {
        // KYPA enrollment — public, no auth required
        const tierMin = { Bronze: 500, Silver: 5000, Gold: 25000, Platinum: 100000 }[form.pledge_tier] || 500;
        const payload = {
          wallet_address: form.agent_address,
          agent_name: form.name,
          description: form.description || '',
          framework: (form.framework || 'custom').toLowerCase(),
          categories: form.capabilities,
          agent_role: form.agent_role,
          merchant_domain: form.agent_role === 'seller' ? form.merchant_domain : '',
          pledge_tier: form.pledge_tier,
          pledge_amount_usdc: tierMin,
          verification_signals: ['wallet', 'domain', 'github'],
          operator_identity: form.website || form.agent_address,
          endpoint_url: form.website || '',
          contact_email: form.contact_email || '',
        };
        const res = await fetch(`${API}/api/kypa/enroll`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (res.ok) { setResult({ ...data.profile, kypa_id: data.kypa_id, mode: 'kypa' }); }
        else { setError(Array.isArray(data.detail) ? data.detail.map((d) => d.msg.replace(/^Value error, /, '')).join(' ') : (data.detail || 'KYPA enrollment failed')); }
      } else {
        const res = await fetch(`${API}/api/agent-credit/agents/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(form),
        });
        const data = await res.json();
        if (res.ok) { setResult(data); }
        else { setError(data.detail || 'Registration failed'); }
      }
    } catch (err) { setError('Network error. Please try again.'); }
    setSubmitting(false);
  };

  if (!token && mode !== 'kypa') {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Login Required</h1>
          <p className="text-gray-500 mb-6">Agent Credit registration requires an account. KYPA Directory enrollment is public and free.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/auth')} className="bg-emerald-600 hover:bg-emerald-700">Log In</Button>
            <Button variant="outline" onClick={() => setMode('kypa')} className="border-white/20">Continue to KYPA (no login)</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet><title>Register Agent — Kojiru Agent Credit Protocol</title></Helmet>
      <Navbar />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Mode Toggle */}
          <div className="flex justify-center mb-6" data-testid="reg-mode-toggle">
            <div className="inline-flex rounded-xl bg-white/[0.03] border border-white/10 p-1">
              <button
                type="button"
                onClick={() => setMode('credit')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'credit' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                data-testid="reg-mode-credit"
              >
                Agent Credit
              </button>
              <button
                type="button"
                onClick={() => setMode('kypa')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  mode === 'kypa' ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
                data-testid="reg-mode-kypa"
              >
                KYPA Directory
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/20 text-white">
                  NEW
                </span>
              </button>
            </div>
          </div>

          <motion.div key={mode} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            {mode === 'kypa' ? (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
                  <ShieldCheck className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-sm text-red-400 font-medium">KYPA Directory Enrollment</span>
                </div>
                <h1 data-testid="register-title" className="text-3xl sm:text-4xl font-bold mb-2">Enroll in the KYPA Directory</h1>
                <p className="text-gray-400 text-sm max-w-lg mx-auto">
                  Join the public registry of AI agents who have pledged skin-in-the-game. Free enrollment. Pledge USDC on Base. Slash-on-harm enforced by code, not committees.
                </p>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
                  <Bot className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Agent Credit Registration</span>
                </div>
                <h1 data-testid="register-title" className="text-3xl sm:text-4xl font-bold mb-2">Register Your Agent</h1>
                <p className="text-gray-400 text-sm">Connect your AI agent to the Kojiru Agent Credit Protocol. Get an ACS score, access credit lines, and join the machine economy.</p>
              </>
            )}
          </motion.div>

          {result ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`p-8 rounded-2xl ${result.mode === 'kypa' ? 'bg-red-500/5 border border-red-500/20' : 'bg-emerald-500/5 border border-emerald-500/20'} text-center`} data-testid="register-success">
              <Check className={`w-12 h-12 mx-auto mb-4 ${result.mode === 'kypa' ? 'text-red-400' : 'text-emerald-400'}`} />
              <h2 className={`text-2xl font-bold mb-2 ${result.mode === 'kypa' ? 'text-red-400' : 'text-emerald-400'}`}>
                {result.mode === 'kypa' ? 'Enrolled in KYPA Directory' : 'Agent Registered'}
              </h2>
              <p className="text-gray-400 mb-4">{form.name} is now part of the Kojiru protocol.</p>
              <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-6">
                <div className="p-3 rounded-xl bg-white/[0.03]">
                  <div className="text-[10px] text-gray-500">{result.mode === 'kypa' ? 'KYPA ID' : 'Agent ID'}</div>
                  <div className="text-xs font-mono text-white truncate">{result.kypa_id || result.agent_id}</div>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03]">
                  <div className="text-[10px] text-gray-500">{result.mode === 'kypa' ? `Tier · ACS` : 'Initial ACS'}</div>
                  <div className={`text-lg font-bold ${result.mode === 'kypa' ? 'text-red-400' : 'text-emerald-400'}`}>
                    {result.mode === 'kypa' ? `${result.pledge_tier || form.pledge_tier} · ${result.acs_score || 700}` : (result.initial_score?.acs || result.initial_score?.score || '-')}
                  </div>
                </div>
              </div>
              {result.mode === 'kypa' && result.agent_role === 'seller' && (
                <div className="mb-6 text-left rounded-xl border border-sky-500/25 bg-sky-500/[0.05] p-4" data-testid="register-seller-domain-status">
                  <div className="text-sm text-white mb-1">Seller agent for <span className="font-mono">{result.merchant_domain}</span>: {result.domain_status === 'confirmed' ? 'Domain confirmed' : 'Domain not yet confirmed'}</div>
                  <div className="text-xs text-gray-400">{result.domain_reason} Open the profile for the snippet the retailer publishes and to check again.</div>
                </div>
              )}
              <div className="flex flex-wrap gap-3 justify-center">
                {result.mode === 'kypa' ? (
                  <>
                    <Button onClick={() => navigate(`/kypa/agent/${result.kypa_id}`)} className="bg-red-500 hover:bg-red-600" data-testid="register-view-profile">View profile</Button>
                    <Button variant="outline" onClick={() => navigate('/kypa/directory')} className="border-white/10" data-testid="register-view-directory">View Directory</Button>
                    <Button variant="outline" onClick={() => navigate('/kypa')} className="border-white/10">Back to KYPA</Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => navigate('/aaas')} className="bg-emerald-600 hover:bg-emerald-700">View Leaderboard</Button>
                    <Button variant="outline" onClick={() => navigate('/developers')} className="border-white/10">Developer Docs</Button>
                  </>
                )}
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
                <h3 className="font-semibold text-sm text-emerald-400 uppercase tracking-wider">Agent Identity</h3>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Agent Wallet Address (ERC-8004) *</label>
                  <input data-testid="reg-address" required value={form.agent_address} onChange={e => setForm({ ...form, agent_address: e.target.value })}
                    placeholder="0x..." className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white font-mono focus:border-emerald-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Agent Name *</label>
                  <input data-testid="reg-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. AlphaTrader-7" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Description</label>
                  <textarea data-testid="reg-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={3} placeholder="What does this agent do? What domains does it specialize in?"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-emerald-500 focus:outline-none resize-y" />
                </div>
              </div>

              <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
                <h3 className="font-semibold text-sm text-emerald-400 uppercase tracking-wider">Technical Profile</h3>
                {mode === 'kypa' && <AgentRoleFields form={form} setForm={setForm} />}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Framework</label>
                  <select data-testid="reg-framework" value={form.framework} onChange={e => setForm({ ...form, framework: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none">
                    <option value="">Select framework...</option>
                    {FRAMEWORKS.map(f => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Capabilities ({form.capabilities.length} selected)</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {CAPABILITY_OPTIONS.map(cap => (
                      <button key={cap} type="button" onClick={() => toggleCap(cap.toLowerCase())}
                        className={`px-2.5 py-1 rounded-lg text-[11px] transition-all ${form.capabilities.includes(cap.toLowerCase()) ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-500 hover:bg-white/10'}`}>
                        {cap}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={customCap} onChange={e => setCustomCap(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomCap())}
                      placeholder="Add custom capability..." className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:border-emerald-500 focus:outline-none" />
                    <button type="button" onClick={addCustomCap} className="px-3 py-1.5 bg-white/10 rounded-lg text-xs text-gray-400 hover:bg-white/15"><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
                <h3 className={`font-semibold text-sm uppercase tracking-wider ${mode === 'kypa' ? 'text-red-300' : 'text-emerald-400'}`}>
                  {mode === 'kypa' ? 'Contact' : 'Staking & Contact'}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {mode !== 'kypa' && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Initial KOJIRU Stake</label>
                      <input data-testid="reg-stake" type="number" min="0" value={form.staked_kutro} onChange={e => setForm({ ...form, staked_kutro: +e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
                      <p className="text-[10px] text-gray-500 mt-1">Collateral for future credit lines. Not required for KYPA.</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Contact Email</label>
                    <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                      placeholder="dev@example.com" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Website</label>
                  <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                    placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
                </div>
                {mode === 'kypa' && (
                  <p className="text-[11px] text-red-300/80 leading-relaxed border-t border-red-500/10 pt-3" data-testid="reg-kypa-no-koji-notice">
                    <strong className="text-red-300">KYPA enrollment does not require KOJI tokens.</strong>
                    &nbsp;Your only collateral is the USDC pledge you select in the next section &mdash; posted directly to your own non-custodial wallet on Base L2.
                  </p>
                )}
              </div>

              {mode === 'kypa' && (
                <div className="p-6 rounded-xl bg-gradient-to-br from-red-500/5 to-emerald-500/5 border border-red-500/20 space-y-4" data-testid="reg-kypa-panel">
                  <h3 className="font-semibold text-sm text-red-300 uppercase tracking-wider flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Select Your Pledge Tier
                  </h3>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Pledged collateral in USDC on Base L2 signals skin-in-the-game to buyers. Higher tiers unlock featured placement. Non-custodial — you retain withdrawal rights subject to slash conditions.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {PLEDGE_TIERS.map((t) => (
                      <button
                        key={t.name}
                        type="button"
                        onClick={() => setForm({ ...form, pledge_tier: t.name })}
                        data-testid={`reg-kypa-tier-${t.name.toLowerCase()}`}
                        className={`text-left p-3 rounded-xl border transition-all bg-gradient-to-br ${t.bg} ${
                          form.pledge_tier === t.name
                            ? `${t.border} ring-2 ring-white/20`
                            : 'border-white/5 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Award className={`w-4 h-4 ${t.color}`} />
                          <span className={`text-xs font-bold uppercase tracking-widest ${t.color}`}>
                            {t.name}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-white">{t.range}</div>
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-xs uppercase tracking-widest text-red-300 font-semibold mb-2">
                      Verification Signals — Add ≥ 2 (all free)
                    </h4>
                    <p className="text-[11px] text-gray-500 leading-relaxed mb-2">
                      Wallet signature is required at claim time. Additional public signals (domain, GitHub, X, ENS, TLS cert, framework attestation) unlock higher Verification Score badges on your profile. No KYC. No document upload.
                    </p>
                    <a
                      href="/kypa#kypa-verification-section"
                      className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
                      data-testid="reg-kypa-verification-link"
                    >
                      Read the full Verification Score model →
                    </a>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <p data-testid="reg-error" className="text-xs text-red-400">{error}</p>
                </div>
              )}

              <Button data-testid="reg-submit" type="submit" disabled={submitting} className={`w-full py-3 text-base ${mode === 'kypa' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Registering...</>
                ) : mode === 'kypa' ? (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Enroll in KYPA Directory</>
                ) : (
                  <><Bot className="w-4 h-4 mr-2" /> Register Agent on Protocol</>
                )}
              </Button>

              <p className="text-[10px] text-gray-600 text-center">
                {mode === 'kypa'
                  ? 'By enrolling, you agree to pledge collateral in USDC on Base L2 (non-custodial, retained by you). Kojiru never holds your funds. Slash conditions apply only to objective failures (T1 payment-without-delivery, T2 bad output proof, T3 endpoint failure). No mediation, no arbitration.'
                  : 'By registering, your agent receives an ERC-8004 on-chain identity and initial Agent Credit Score (ACS). Staked USDC on Base serves as collateral for future credit lines.'}
              </p>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
